import moment from 'moment';

import { AUTOMATION_TRIGGER_TYPES } from '../../start-date-triggered-communication/automation-trigger-types';

import { CreatableApplicantAutomationTypes } from './applicant-automation-communication-types';

import { MESSAGE_TYPE } from '../../util/message-types';
import { START_DATE_TRIGGERED_COMMUNICATION_TYPES } from '../../util/start-date-triggered-communication-types';

export const isEmployeeAutomationTrigger = (
  trigger:
    | 'applicant'
    | CreatableApplicantAutomationTypes
    | StartDateTriggeredCommunicationTriggerType,
): trigger is StartDateTriggeredCommunicationTriggerType =>
  Object.values(AUTOMATION_TRIGGER_TYPES).includes(
    trigger as StartDateTriggeredCommunicationTriggerType,
  );

export const getSendTime = (sdtc: StartDateTriggeredCommunication) => {
  const sendDate = moment(sdtc.sendAt, 'HH:mm:ss');

  return sdtc.sendInterval && sdtc.sendIntervalType
    ? sendDate
        .add(
          sdtc.sendInterval,
          sdtc.sendIntervalType.toLowerCase() as 'day' | 'week' | 'month' | 'year',
        )
        .format()
    : sendDate.format();
};

export const getMessageType = (sdtc: StartDateTriggeredCommunication) => {
  switch (sdtc.communicationType) {
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.SURVEY:
      return MESSAGE_TYPE.TEXT;
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT:
      return MESSAGE_TYPE.EMAIL;
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE:
      return sdtc.StartDateTriggeredMessage?.messageType;
    default:
      throw new Error(`unrecognized sdtc type: ${sdtc.communicationType}`);
  }
};

export const getMessageBody = (sdtc: StartDateTriggeredCommunication): string => {
  let messageBody: string | undefined;
  switch (sdtc.communicationType) {
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.SURVEY:
      messageBody = sdtc.Survey?.messageBody;
      break;
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT:
      messageBody = sdtc.StartDateTriggeredAlert?.messageBody;
      break;
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE:
      messageBody = sdtc.StartDateTriggeredMessage?.messageBody;
      break;
    default:
      throw new Error(`unrecognized sdtc type: ${sdtc.communicationType}`);
  }

  if (messageBody === undefined) {
    throw new Error(`Message body missing from sdtc ${sdtc.startDateTriggeredCommunicationId}`);
  }
  return messageBody;
};

export const getSubject = (sdtc: StartDateTriggeredCommunication) => {
  if (sdtc.communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT) {
    return sdtc.StartDateTriggeredAlert?.subject;
  }
  if (
    sdtc.communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE &&
    sdtc.StartDateTriggeredMessage?.messageType === MESSAGE_TYPE.EMAIL
  ) {
    return sdtc.StartDateTriggeredMessage.subject;
  }

  return undefined;
};

export const getOtherLanguages = (sdtc: StartDateTriggeredCommunication) => {
  switch (sdtc.communicationType) {
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.SURVEY:
      return sdtc.Survey?.spanishMessageBody
        ? [
            {
              languageCode: 'es',
              messageBody: sdtc.Survey.spanishMessageBody,
            },
          ]
        : [];
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT:
      return sdtc.StartDateTriggeredAlert?.spanishMessageBody
        ? [
            {
              languageCode: 'es',
              messageBody: sdtc.StartDateTriggeredAlert.spanishMessageBody,
              subject: sdtc.StartDateTriggeredAlert.spanishSubject,
            },
          ]
        : [];
    case START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE:
      return sdtc.StartDateTriggeredMessage?.spanishMessageBody
        ? [
            {
              languageCode: 'es',
              messageBody: sdtc.StartDateTriggeredMessage.spanishMessageBody,
              subject: sdtc.StartDateTriggeredMessage.spanishSubject || undefined,
            },
          ]
        : [];
    default:
      throw new Error(`unrecognized sdtc type: ${sdtc.communicationType}`);
  }
};

export const getAlertRecipients = (sdtc: StartDateTriggeredCommunication) => {
  if (!sdtc.StartDateTriggeredAlert) {
    return [];
  }

  const alertRecipients = sdtc.StartDateTriggeredAlert.StartDateTriggeredAlertRecipients?.map(
    (s) => s.User && `${s.User.firstName} ${s.User.lastName}`,
  );
  return sdtc.StartDateTriggeredAlert?.sendToSupervisors
    ? (alertRecipients || []).concat(["the employee's supervisor(s)"])
    : alertRecipients;
};

export const getAlertEmployeeRecipients = ({
  employees,
  alertRecipients,
  sendToSupervisors,
  selectedGroupIds,
}: {
  employees: Employee[];
  alertRecipients?: Array<{ username: string }>;
  sendToSupervisors?: boolean;
  selectedGroupIds?: number[];
}) => {
  const recipientsById: Record<number, Employee> = {};
  alertRecipients?.forEach((u) => {
    const employee = employees.find((e) => u.username === e.username);
    if (employee) {
      recipientsById[employee.employeeId] = employee;
    }
  });
  if (sendToSupervisors) {
    const selectedEmployees = selectedGroupIds?.length
      ? employees.filter((e) =>
          selectedGroupIds?.some((gId) =>
            e.EmployeeGroups.find((group) => group.employeeGroupId === gId),
          ),
        )
      : employees;
    selectedEmployees.forEach((e) => {
      e.Supervisors.forEach((s) => {
        recipientsById[s.employeeId] = s;
      });
    });
  }

  return Object.values(recipientsById);
};
